import { Film, ImageOff, Pin, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { Locale, PublicPost } from "@vzlive/shared";
import { t } from "@vzlive/shared/i18n";
import { ConsentEmbed } from "./ConsentEmbed";
import { partitionTimeline } from "../lib/timeline";
import { linkify } from "../lib/linkify";

function timeLabel(
  date: string,
  locale: Locale,
  relative: boolean,
  timezone?: string,
) {
  const value = new Date(date);
  if (!relative) {
    return new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: timezone,
    }).format(value);
  }
  const minutes = Math.round((value.getTime() - Date.now()) / 60_000);
  if (Math.abs(minutes) < 60) {
    return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(
      minutes,
      "minute",
    );
  }
  const hours = Math.round(minutes / 60);
  return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(
    hours,
    "hour",
  );
}

function TimelinePost({
  post,
  locale,
  relative,
  pinned,
  compact,
  timezone,
  onOpen,
}: {
  post: PublicPost;
  locale: Locale;
  relative: boolean;
  pinned?: boolean;
  compact?: boolean;
  timezone?: string;
  onOpen: (post: PublicPost, trigger: HTMLButtonElement) => void;
}) {
  const image = post.media?.derivatives
    .filter((item) => item.format !== "original")
    .sort((a, b) => a.width - b.width);
  const fallback = image?.at(-1);

  return (
    <article
      className={`timeline-post ${pinned ? "timeline-post--pinned" : ""} ${
        compact ? "timeline-post--compact" : ""
      }`}
    >
      <div className="timeline-post__rail">
        <time
          dateTime={post.publishedAt}
          title={new Intl.DateTimeFormat(locale, {
            dateStyle: "full",
            timeStyle: "short",
            timeZone: timezone,
          }).format(new Date(post.publishedAt))}
        >
          {timeLabel(post.publishedAt, locale, relative, timezone)}
        </time>
        <i aria-hidden="true" />
      </div>
      <div className="timeline-post__body">
        <div className="timeline-post__message">
          {pinned && (
            <span className="pin-label">
              <Pin size={15} aria-hidden="true" /> {t(locale, "pinned")}
            </span>
          )}
          {post.byline && (
            <p className="timeline-post__author">{post.byline}</p>
          )}
          {post.content && (
            <p className="timeline-post__copy">{linkify(post.content)}</p>
          )}
        </div>
        {post.type === "image" &&
          (fallback ? (
            (() => {
              const thumb =
                image?.find((item) => item.width >= 320) ?? fallback;
              const portrait =
                thumb.height && thumb.width > 0
                  ? thumb.height / thumb.width > 1.05
                  : false;
              const sizes = compact
                ? "(max-width: 540px) 100vw, 600px"
                : "(max-width: 720px) 100vw, 360px";
              return (
                <button
                  type="button"
                  className={`timeline-media${
                    compact ? " timeline-post__wide timeline-media--wide" : ""
                  }${portrait ? " timeline-media--portrait" : ""}`}
                  onClick={(e) => onOpen(post, e.currentTarget)}
                  aria-label={
                    locale === "en" ? "Enlarge image" : "Bild vergrößern"
                  }
                >
                  <picture>
                    <source
                      type="image/avif"
                      srcSet={image
                        ?.filter((item) => item.format === "avif")
                        .map((item) => `${item.url} ${item.width}w`)
                        .join(", ")}
                      sizes={sizes}
                    />
                    <source
                      type="image/webp"
                      srcSet={image
                        ?.filter((item) => item.format === "webp")
                        .map((item) => `${item.url} ${item.width}w`)
                        .join(", ")}
                      sizes={sizes}
                    />
                    <img
                      src={thumb.url}
                      alt={post.media?.alt ?? ""}
                      width={thumb.width ?? undefined}
                      height={thumb.height || undefined}
                      loading="lazy"
                    />
                  </picture>
                  <span className="timeline-media__zoom" aria-hidden="true">
                    ↗
                  </span>
                </button>
              );
            })()
          ) : (
            <div
              className={`media-placeholder ${
                compact ? "timeline-post__wide" : ""
              }`}
            >
              <ImageOff size={22} aria-hidden="true" />{" "}
              {locale === "en"
                ? "Image is being processed"
                : "Bild wird verarbeitet"}
            </div>
          ))}
        {post.type === "video" ? (
          post.video?.state === "ready" && post.embed ? (
            <div className={compact ? "timeline-post__wide" : ""}>
              <ConsentEmbed embed={post.embed} locale={locale} />
            </div>
          ) : (
            <div
              className={`video-state ${compact ? "timeline-post__wide" : ""}`}
              role="status"
            >
              <Film size={20} aria-hidden="true" />
              <span>
                {t(
                  locale,
                  post.video &&
                    (post.video.state === "failed" ||
                      post.video.state === "removed")
                    ? "videoFailed"
                    : "videoProcessing",
                )}
              </span>
            </div>
          )
        ) : post.embed ? (
          <div className={compact ? "timeline-post__wide" : ""}>
            <ConsentEmbed embed={post.embed} locale={locale} />
          </div>
        ) : null}
      </div>
    </article>
  );
}

export function Timeline({
  posts,
  locale,
  relative,
  compact = false,
  timezone,
}: {
  posts: PublicPost[];
  locale: Locale;
  relative: boolean;
  compact?: boolean;
  timezone?: string;
}) {
  const { pinned, regular } = partitionTimeline(posts);
  const [lightbox, setLightbox] = useState<PublicPost | null>(null);
  const lightboxRef = useRef<HTMLDialogElement>(null);
  const lastTrigger = useRef<HTMLElement | null>(null);

  const openLightbox = (post: PublicPost, trigger: HTMLButtonElement) => {
    lastTrigger.current = trigger;
    setLightbox(post);
    lightboxRef.current?.showModal();
  };

  // Nach dem Schließen landet der Fokus wieder auf dem auslösenden Bild.
  useEffect(() => {
    const dialog = lightboxRef.current;
    if (!dialog) return;
    const onClose = () => lastTrigger.current?.focus();
    dialog.addEventListener("close", onClose);
    return () => dialog.removeEventListener("close", onClose);
  }, []);

  if (!posts.length) {
    return <div className="timeline-empty">{t(locale, "noPosts")}</div>;
  }

  const en = locale === "en";

  return (
    <>
      <ol className="timeline-list">
        {pinned.map((post) => (
          <li key={post.id}>
            <TimelinePost
              timezone={timezone}
              post={post}
              locale={locale}
              relative={relative}
              pinned
              compact={compact}
              onOpen={openLightbox}
            />
          </li>
        ))}
        {regular.map((post) => (
          <li key={post.id}>
            <TimelinePost
              timezone={timezone}
              post={post}
              locale={locale}
              relative={relative}
              compact={compact}
              onOpen={openLightbox}
            />
          </li>
        ))}
      </ol>
      <dialog
        ref={lightboxRef}
        className="pulse-dialog pulse-dialog--image"
        aria-label={en ? "Image view" : "Bildansicht"}
        onClick={(e) => {
          if (e.target === e.currentTarget) lightboxRef.current?.close();
        }}
      >
        <div className="pulse-dialog__head">
          <h2>{en ? "Image view" : "Bildansicht"}</h2>
          <button
            type="button"
            className="pulse-dialog__close"
            aria-label={en ? "Close image view" : "Bildansicht schließen"}
            onClick={() => lightboxRef.current?.close()}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        {lightbox?.media && (
          <div className="pulse-dialog__image">
            <img
              src={
                lightbox.media.derivatives.at(-1)?.url ??
                lightbox.media.derivatives[0]?.url
              }
              alt={lightbox.media.alt ?? ""}
            />
          </div>
        )}
        <p className="pulse-dialog__caption">
          {lightbox?.media?.credit
            ? `${en ? "Photo" : "Foto"}: ${lightbox.media.credit}`
            : (lightbox?.media?.alt ?? "")}
        </p>
      </dialog>
    </>
  );
}
